import React, { useState, useEffect } from 'react';
import { useWorkflow } from '../contexts/WorkflowContext';
import './Modal.css';

const NodeSettingsModal = ({ node, onClose }) => {
  const [settings, setSettings] = useState({});
  const { updateNode } = useWorkflow();

  useEffect(() => {
    if (node) {
      setSettings(node.settings || {});
    }
  }, [node]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!node) return;

    try {
      await updateNode(node.id, { settings });
      onClose();
    } catch (error) {
      alert('Failed to update node: ' + error.message);
    }
  };

  const handleClose = () => {
    setSettings({});
    onClose();
  };

  const renderSettingsForm = () => {
    if (!node) return null;

    switch (node.type) {
      case 'text_prompt':
        return (
          <div className="form-group">
            <label htmlFor="prompt">Text Prompt:</label>
            <textarea
              id="prompt"
              value={settings.prompt || ''}
              onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
              placeholder="Enter your text prompt"
              rows="4"
              required
            />
          </div>
        );

      case 'openai_gpt4':
        return (
          <>
            <div className="form-group">
              <label htmlFor="apiKey">OpenAI API Key:</label>
              <input
                type="password"
                id="apiKey"
                value={settings.apiKey || ''}
                onChange={(e) => setSettings({ ...settings, apiKey: e.target.value })}
                placeholder="Enter your OpenAI API key"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="model">Model:</label>
              <select
                id="model"
                value={settings.model || 'gpt-4'}
                onChange={(e) => setSettings({ ...settings, model: e.target.value })}
              >
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="maxTokens">Max Tokens:</label>
              <input
                type="number"
                id="maxTokens"
                value={settings.maxTokens || 1000}
                onChange={(e) => setSettings({ ...settings, maxTokens: parseInt(e.target.value) })}
                min="1"
                max="4000"
              />
            </div>
          </>
        );

      case 'anthropic_claude':
        return (
          <>
            <div className="form-group">
              <label htmlFor="apiKey">Anthropic API Key:</label>
              <input
                type="password"
                id="apiKey"
                value={settings.apiKey || ''}
                onChange={(e) => setSettings({ ...settings, apiKey: e.target.value })}
                placeholder="Enter your Anthropic API key"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="model">Model:</label>
              <select
                id="model"
                value={settings.model || 'claude-3-sonnet-20240229'}
                onChange={(e) => setSettings({ ...settings, model: e.target.value })}
              >
                <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
                <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="maxTokens">Max Tokens:</label>
              <input
                type="number"
                id="maxTokens"
                value={settings.maxTokens || 1000}
                onChange={(e) => setSettings({ ...settings, maxTokens: parseInt(e.target.value) })}
                min="1"
                max="4000"
              />
            </div>
          </>
        );

      case 'elevenlabs_tts':
        return (
          <>
            <div className="form-group">
              <label htmlFor="apiKey">ElevenLabs API Key:</label>
              <input
                type="password"
                id="apiKey"
                value={settings.apiKey || ''}
                onChange={(e) => setSettings({ ...settings, apiKey: e.target.value })}
                placeholder="Enter your ElevenLabs API key"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="voice">Voice:</label>
              <select
                id="voice"
                value={settings.voice || 'professional'}
                onChange={(e) => setSettings({ ...settings, voice: e.target.value })}
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="dramatic">Dramatic</option>
                <option value="casual">Casual</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="speed">Speed:</label>
              <input
                type="range"
                id="speed"
                min="0.5"
                max="2.0"
                step="0.1"
                value={settings.speed || 1.0}
                onChange={(e) => setSettings({ ...settings, speed: parseFloat(e.target.value) })}
              />
              <span>{settings.speed || 1.0}x</span>
            </div>
          </>
        );

      case 'stable_video':
        return (
          <>
            <div className="form-group">
              <label htmlFor="prompt">Video Prompt:</label>
              <textarea
                id="prompt"
                value={settings.prompt || ''}
                onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
                placeholder="Describe the video you want to generate"
                rows="3"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="duration">Duration (seconds):</label>
              <input
                type="number"
                id="duration"
                value={settings.duration || 30}
                onChange={(e) => setSettings({ ...settings, duration: parseInt(e.target.value) })}
                min="1"
                max="120"
              />
            </div>
            <div className="form-group">
              <label htmlFor="style">Style:</label>
              <select
                id="style"
                value={settings.style || 'animated'}
                onChange={(e) => setSettings({ ...settings, style: e.target.value })}
              >
                <option value="animated">Animated</option>
                <option value="realistic">Realistic</option>
                <option value="artistic">Artistic</option>
                <option value="cartoon">Cartoon</option>
              </select>
            </div>
          </>
        );

      default:
        return (
          <div className="form-group">
            <p>No specific settings available for this node type.</p>
          </div>
        );
    }
  };

  if (!node) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Node Settings - {node.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
          <button className="close-btn" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {renderSettingsForm()}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NodeSettingsModal;
